import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, GripVertical, Trash2, Flame, Inbox } from "lucide-react";
import AnimatedValue from "@/components/AnimatedValue";
import EmptyState from "@/components/EmptyState";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

function currency(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
}

export default function Pipelines() {
  const [pipelines, setPipelines] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [creatingDeal, setCreatingDeal] = useState(false);
  const [dealForm, setDealForm] = useState({ title: "", value: 0, stage: "", contact_id: "", expected_close: "", notes: "" });
  const [creatingPipe, setCreatingPipe] = useState(false);
  const [pipeName, setPipeName] = useState("");
  const [dragId, setDragId] = useState(null);

  const active = useMemo(() => pipelines.find((p) => p.id === activeId), [pipelines, activeId]);

  const load = async () => {
    const { data } = await api.get("/pipelines");
    setPipelines(data);
    if (data.length && !activeId) setActiveId(data[0].id);
  };

  const loadDeals = async (pid) => {
    if (!pid) return;
    const { data } = await api.get("/deals", { params: { pipeline_id: pid } });
    setDeals(data);
  };

  useEffect(() => { load(); api.get("/contacts").then(({ data }) => setContacts(data)); }, []);
  useEffect(() => { if (activeId) loadDeals(activeId); }, [activeId]);

  const openNewDeal = (stage) => {
    setDealForm({ title: "", value: 0, stage, contact_id: "", expected_close: "", notes: "" });
    setCreatingDeal(true);
  };

  const submitDeal = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...dealForm,
        value: Number(dealForm.value) || 0,
        pipeline_id: activeId,
        contact_id: dealForm.contact_id || null,
        expected_close: dealForm.expected_close || null,
      };
      await api.post("/deals", payload);
      setCreatingDeal(false);
      loadDeals(activeId);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const moveDeal = async (dealId, stage) => {
    setDeals((prev) => prev.map((d) => d.id === dealId ? { ...d, stage } : d));
    try {
      await api.patch(`/deals/${dealId}/move`, { stage });
    } catch (e) {
      toast.error("Move failed");
      loadDeals(activeId);
    }
  };

  const deleteDeal = async (id) => {
    if (!confirm("Delete this deal?")) return;
    try {
      await api.delete(`/deals/${id}`);
      setDeals((prev) => prev.filter((d) => d.id !== id));
    } catch (e) { toast.error("Delete failed"); }
  };

  const createPipeline = async (e) => {
    e.preventDefault();
    if (!pipeName.trim()) return;
    try {
      const { data } = await api.post("/pipelines", { name: pipeName });
      setPipelines((p) => [...p, data]);
      setActiveId(data.id);
      setCreatingPipe(false); setPipeName("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const stageTotal = (stage) => deals.filter((d) => d.stage === stage).reduce((s, d) => s + (d.value || 0), 0);

  return (
    <div className="p-6 md:p-10" data-testid="pipelines-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="section-label">Revenue engine</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase mt-1">Pipelines</h1>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="pipeline-select"
            value={activeId}
            onChange={(e) => setActiveId(e.target.value)}
            className="border-2 border-[var(--peak-ink)] bg-white px-3 py-2 text-sm font-bold uppercase tracking-widest"
          >
            {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button data-testid="new-pipeline-btn" onClick={() => setCreatingPipe(true)} className="border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase tracking-widest px-4 py-2 text-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Pipeline
          </button>
        </div>
      </div>

      {active && (
        <div className="flex gap-4 overflow-x-auto pb-4 tick-scroll" data-testid="kanban-board">
          {active.stages.map((stage) => {
            const stageDeals = deals.filter((d) => d.stage === stage);
            return (
              <div
                key={stage}
                className="w-[300px] shrink-0 bg-[var(--peak-surface-alt)] border border-[var(--peak-border)] flex flex-col"
                data-testid={`kanban-column-${stage}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => dragId && moveDeal(dragId, stage)}
              >
                <div className="p-3 border-b border-[var(--peak-border)] flex items-center justify-between">
                  <div>
                    <div className="font-heading font-bold uppercase text-sm tracking-tighter">{stage}</div>
                    <div className="section-label"><AnimatedValue value={stageDeals.length} /> · <AnimatedValue value={currency(stageTotal(stage))} /></div>
                  </div>
                  <button
                    data-testid={`add-deal-${stage}`}
                    onClick={() => openNewDeal(stage)}
                    className="w-7 h-7 bg-[var(--peak-accent)] text-white flex items-center justify-center hover:bg-black"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-2 flex flex-col gap-2 min-h-[200px]">
                  {stageDeals.map((d) => {
                    const contact = contacts.find((c) => c.id === d.contact_id);
                    return (
                      <div
                        key={d.id}
                        draggable
                        onDragStart={() => setDragId(d.id)}
                        onDragEnd={() => setDragId(null)}
                        className="bg-white border border-[var(--peak-border)] p-3 cursor-grab hover:border-[var(--peak-ink)] group"
                        data-testid={`deal-card-${d.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-semibold text-sm leading-tight">{d.title}</div>
                          <div className="flex items-center gap-1">
                            {contact && typeof contact.score === "number" && contact.score >= 70 && (
                              <span className="inline-flex items-center gap-0.5 bg-[var(--peak-accent)] text-white px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest" data-testid={`deal-hot-${d.id}`}>
                                <Flame className="w-2.5 h-2.5" /> HOT
                              </span>
                            )}
                            <button onClick={() => deleteDeal(d.id)} data-testid={`delete-deal-${d.id}`} className="opacity-0 group-hover:opacity-100 hover:text-[#E50000]">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 font-heading font-black text-xl tracking-tighter">{currency(d.value)}</div>
                        {contact && (
                          <div className="mt-1 section-label truncate">{(contact.first_name || "") + " " + (contact.last_name || "")}</div>
                        )}
                        {d.expected_close && (
                          <div className="section-label mt-1">Close: {d.expected_close}</div>
                        )}
                        <div className="mt-2 flex items-center gap-1 text-[var(--peak-muted)]">
                          <GripVertical className="w-3 h-3" />
                          <span className="text-[10px] font-mono uppercase">drag to move</span>
                        </div>
                      </div>
                    );
                  })}
                  {stageDeals.length === 0 && (
                    <EmptyState icon={Inbox} hint="Drop deals here" testId={`kanban-empty-${stage}`} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New deal dialog */}
      <Dialog open={creatingDeal} onOpenChange={setCreatingDeal}>
        <DialogContent className="bg-white border-2 border-[var(--peak-ink)] rounded-none" data-testid="new-deal-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tighter uppercase">New deal · {dealForm.stage}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitDeal} className="space-y-4">
            <div>
              <label className="section-label">Title</label>
              <input required data-testid="deal-title" className="brutalist-input" value={dealForm.title} onChange={(e) => setDealForm({ ...dealForm, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="section-label">Value (USD)</label>
                <input type="number" data-testid="deal-value" className="brutalist-input" value={dealForm.value} onChange={(e) => setDealForm({ ...dealForm, value: e.target.value })} />
              </div>
              <div>
                <label className="section-label">Expected close</label>
                <input type="date" data-testid="deal-close" className="brutalist-input" value={dealForm.expected_close} onChange={(e) => setDealForm({ ...dealForm, expected_close: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="section-label">Contact (optional)</label>
              <select data-testid="deal-contact" className="brutalist-input" value={dealForm.contact_id} onChange={(e) => setDealForm({ ...dealForm, contact_id: e.target.value })}>
                <option value="">—</option>
                {contacts.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} · {c.email}</option>)}
              </select>
            </div>
            <div>
              <label className="section-label">Notes</label>
              <textarea rows={3} data-testid="deal-notes" className="w-full border border-[var(--peak-border)] p-2" value={dealForm.notes} onChange={(e) => setDealForm({ ...dealForm, notes: e.target.value })} />
            </div>
            <DialogFooter className="flex gap-2 sm:justify-end">
              <button type="button" onClick={() => setCreatingDeal(false)} className="px-4 py-2 border-2 border-[var(--peak-ink)] font-bold uppercase text-xs tracking-widest hover:bg-black hover:text-white flex items-center gap-2">
                <X className="w-4 h-4" /> Cancel
              </button>
              <button type="submit" data-testid="submit-deal" className="px-6 py-2 bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest">Create deal</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New pipeline dialog */}
      <Dialog open={creatingPipe} onOpenChange={setCreatingPipe}>
        <DialogContent className="bg-white border-2 border-[var(--peak-ink)] rounded-none" data-testid="new-pipeline-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tighter uppercase">New pipeline</DialogTitle>
          </DialogHeader>
          <form onSubmit={createPipeline} className="space-y-4">
            <div>
              <label className="section-label">Name</label>
              <input required data-testid="pipeline-name" className="brutalist-input" value={pipeName} onChange={(e) => setPipeName(e.target.value)} />
              <div className="section-label mt-2">Default stages: New · Contacted · Qualified · Proposal · Won · Lost</div>
            </div>
            <DialogFooter className="flex gap-2 sm:justify-end">
              <button type="submit" data-testid="submit-pipeline" className="px-6 py-2 bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest">Create</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
